<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { YButton, YSpinner } from '@yakimachief/ych-ts-component-library'
import { useGuestStore } from '@/stores/GuestStore'
import { useUserStore } from '@/stores/UserStore'

const route = useRoute()
const guestStore = useGuestStore()
const userStore = useUserStore()

const isGuest = computed(() => userStore.currentUser?.isGuest === true)
const confirmingDone = ref(false)

// On a shared device, "Done" returns to the same test or contest so the next person
// gets a clean guest form.
const returnPath = computed(() => {
  const { testCode, contestId } = route.params
  if (typeof testCode === 'string' && testCode !== '')
    return '/test/' + encodeURIComponent(testCode)
  if (typeof contestId === 'string' && contestId !== '')
    return '/contest/' + encodeURIComponent(contestId)
  return '/login'
})

function finish() {
  confirmingDone.value = false
  guestStore.finish(returnPath.value)
}

const claimFailed = computed(() => guestStore.outcome === 'claim_failed')
</script>

<template>
  <div v-if="isGuest" class="guest-bar" role="region" aria-label="Guest session">
    <div class="guest-bar__inner">
      <span class="guest-bar__label">
        Guest: {{ userStore.currentUser?.firstName }} {{ userStore.currentUser?.lastName }}
      </span>
      <div class="guest-bar__actions">
        <y-spinner v-if="guestStore.actionInProgress" width="2rem" />
        <template v-else>
          <y-button
            label="Keep my account"
            size="sm"
            width="auto"
            @click="guestStore.keepAccount()"
          ></y-button>
          <y-button
            label="Done"
            type="secondary"
            size="sm"
            width="auto"
            @click="confirmingDone = true"
          ></y-button>
        </template>
      </div>
    </div>
    <p v-if="claimFailed" class="guest-bar__error" role="alert">{{ guestStore.message }}</p>

    <Dialog :open="confirmingDone" class="guest-confirm" @close="confirmingDone = false">
      <div class="guest-confirm__backdrop" aria-hidden="true" />
      <div class="guest-confirm__wrap">
        <DialogPanel class="guest-confirm__panel">
          <DialogTitle class="guest-confirm__title">Sign out?</DialogTitle>
          <p class="guest-confirm__text">
            You'll be signed out of this guest session so the next person can sign in.
          </p>
          <div class="guest-confirm__actions">
            <y-button
              label="Cancel"
              type="secondary"
              size="sm"
              width="auto"
              @click="confirmingDone = false"
            ></y-button>
            <y-button label="Sign out" size="sm" width="auto" @click="finish"></y-button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
.guest-bar {
  background: var(--ych-grey-100, #f3f3f3);
  border-bottom: 1px solid var(--ych-grey-200, #ddd);
  padding: 0.375rem 1rem;

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    max-width: 48rem;
    margin: 0 auto;
  }

  &__label {
    font-size: 0.875rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
    align-items: center;

    :deep(.y-button__button) {
      white-space: nowrap;
    }
  }

  &__error {
    max-width: 48rem;
    margin: 0.5rem auto 0;
    color: var(--primary-brand-color);
    font-size: 0.875rem;
  }
}

.guest-confirm {
  position: relative;
  z-index: 50;

  &__backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
  }

  &__wrap {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  &__panel {
    width: 100%;
    max-width: 22rem;
    background: #fff;
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 10px 30px rgb(0 0 0 / 20%);
  }

  &__title {
    font-weight: var(--y-font-weight-medium, 600);
    font-size: 1.125rem;
    color: var(--secondary-brand-color);
  }

  &__text {
    margin-top: 0.5rem;
    font-size: 0.875rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.25rem;
  }
}
</style>
